import { defineStore } from "pinia";
import apiClient from "@/core/buildRequest";
import router from "@/router/index.js";
import { useGeneralStore } from "@/store/General";
import { DEVICE_UUID_STORAGE_KEY, getDeviceUuid } from "@/core/deviceId";
function storeToken(service, token, expireDate) {
  localStorage.setItem(`${service}_token`, token);
  localStorage.setItem(`${service}TokenExpireAt`, expireDate);
}

export const loginForm = defineStore("logIn", {
  state: () => ({
    accounts: [],
    role: "",
    permissions: [],
    pendingLogin: null,
    user: {
      email: "guest@guest.com",
      id: 2,
      image: "https://backend.promxa.com/storage/images/users/default.png",
      is_notify: true,
      lang: "English",
      name: "Mr Ahmed Zidan 22",
      token: "149|Re3AvUkQalFwnAGvxhgFdaHz3V3W9xaYDN7V1yjZ",
      role: "Super Admin",
    },
  }),
  getters: {
    getAccounts: (state) => state.accounts,
  },
  actions: {
    getServerMessage(response) {
      return (
        response?.data?.msg ||
        response?.data?.message ||
        "An unexpected error occurred. Please try again later."
      );
    },
    completeLogin(data) {
      const token = data?.data?.token;

      if (!token) {
        throw new Error("The server did not return an authentication token.");
      }

      localStorage.setItem("user", JSON.stringify(data));
      localStorage.setItem("user_token", token);
      useGeneralStore().showSnackbar("Login Successfully", "#237276");
      this.getRolesPermissions();
      this.getCredentials();
      router.push({ name: "HomePage" });
    },
    clearPendingLogin() {
      this.pendingLogin = null;
    },
    async checkAuth() {
      let status = false;
      if (localStorage.getItem("user_token")) {
        const { data } = await apiClient.get("/check-user-token");
        if (data.data.user_id !== null) {
          let c = JSON.parse(localStorage.getItem("user"));
          status = c["data"].id === data.data.user_id;
        }
      }
      if (!status) {
        for (const key of Object.keys(localStorage)) {
          if (key !== DEVICE_UUID_STORAGE_KEY && key !== "lang") {
            localStorage.removeItem(key);
          }
        }
      }
      return status;
    },
    async getAllAccounts() {
      try {
        const { data } = await apiClient.get("/get-user-accounts");
        this.accounts = data.data;
      } catch (error) {
        this.accounts = [];
        console.error("Unable to load saved accounts:", error);
      }
    },
    async getCredentials() {
      try {
        const { data } = await apiClient.get("/get-credentials");
        const credentials = data.data;

        [
          "snapchat",
          "tiktok",
          "facebook",
          "instagram",
          "twitter",
          "google_ads",
          "drive",
        ].forEach((service) => {
          if (credentials[service]) {
            storeToken(
              service,
              credentials[service].access_token,
              credentials[service].expire_date
            );
          }
        });
      } catch (error) {
        console.error("Error fetching credentials:", error);
      }
    },
    async getRolesPermissions() {
      if (localStorage.getItem("user_token")) {
        try {
          const { data } = await apiClient.get("/get-user-roles");
          this.role = data.data.name;

          this.permissions = Object.values(data.data.roles)
            .flat()
            .map((item) => item.role_name);

        } catch (error) {
          console.error("Login failed:", error);
        }
      }
    },
    async login(email, password) {
      try {
        const deviceId = getDeviceUuid();

        const response = await apiClient.post("/sign-in", {
          email,
          password,
          device_type: "web",
          uuid_device: deviceId,
          device_id: deviceId,
        });

        const responseKey = response.data?.key || response.data?.data?.key;
        if (response.status === 202 && responseKey === "email_otp_required") {
          const otherSessionActive = Boolean(
            response.data?.data?.other_session_active
          );
          this.pendingLogin = {
            email,
            password,
            uuidDevice: deviceId,
            otherSessionActive,
          };
          if (otherSessionActive) {
            useGeneralStore().showSnackbar(
              response.data?.msg ||
                "You are already signed in on another device. Continuing here will sign you out of that device.",
              "warning"
            );
          }
          router.push({ name: "LoginOtp" });
          return { otpRequired: true, otherSessionActive };
        }

        if (response.status === 200) {
          this.completeLogin(response.data);
          return { otpRequired: false };
        }

        useGeneralStore().showSnackbar(this.getServerMessage(response), "error");
        return { otpRequired: false };
      } catch (error) {
        console.error("Login failed:", error);
        return { otpRequired: false, error: true };
      }
    },
    async verifyLoginOtp(code) {
      if (!this.pendingLogin) {
        router.replace({ name: "LoginPage" });
        return { success: false, message: "Please sign in again." };
      }

      try {
        const { email, uuidDevice } = this.pendingLogin;
        const response = await apiClient.post("/verify-login-otp", {
          email,
          code,
          device_id: uuidDevice,
          uuid_device: uuidDevice,
          device_type: "web",
        });

        this.clearPendingLogin();
        this.completeLogin(response.data);
        return { success: true };
      } catch (error) {
        console.error("OTP verification failed:", error);
        return {
          success: false,
          message:
            error.response?.data?.msg ||
            error.response?.data?.message ||
            "The verification code is invalid or expired.",
        };
      }
    },
    async resendLoginOtp() {
      if (!this.pendingLogin) {
        return { success: false, message: "Please sign in again." };
      }

      try {
        const { email, password, uuidDevice } = this.pendingLogin;
        const response = await apiClient.post("/resend-login-otp", {
          email,
          password,
          uuid_device: uuidDevice,
        });
        const otherSessionActive = Boolean(
          response.data?.data?.other_session_active
        );
        if (this.pendingLogin) {
          this.pendingLogin.otherSessionActive = otherSessionActive;
        }
        const message = this.getServerMessage(response);
        useGeneralStore().showSnackbar(message, "#237276");
        return { success: true, message, otherSessionActive };
      } catch (error) {
        console.error("OTP resend failed:", error);
        return {
          success: false,
          message:
            error.response?.data?.msg ||
            error.response?.data?.message ||
            "Unable to resend the verification code.",
        };
      }
    },
    async LoginByAccountId(id) {
      try {
        const deviceId = getDeviceUuid();
        const { data } = await apiClient.post("/login-as-account", {
          device_id: deviceId,
          uuid_device: deviceId,
          user_id: id,
          device_type: "web",
        });
        localStorage.setItem("user", JSON.stringify(data));
        localStorage.setItem("user_token", data.data.token);

        useGeneralStore().showSnackbar("Login Successfully", "#237276");

        this.getRolesPermissions();
        this.getCredentials();
        router.push({ name: "HomePage" });
      } catch (error) {
        console.error("Login failed:", error);
      }
    },
    async logout() {
      try {
        await apiClient.get("/sign-out");
        for (const key of Object.keys(localStorage)) {
          if (key !== DEVICE_UUID_STORAGE_KEY && key !== "lang") {
            localStorage.removeItem(key);
          }
        }

        useGeneralStore().showSnackbar("Logout Successfully", "#237276");

        router.push({ name: "LoginPage" });
      } catch (error) {
        console.error("Logout failed:", error);
      }
    },
    async deleteOldAccount(id) {
      try {
        const { data } = await apiClient.post("/delete-user-device", {
          device_id: getDeviceUuid(),
          user_id: id,
        });
        this.getAllAccounts();
        router.push({ name: "Login" });
      } catch (error) {
        console.error("Logout failed:", error);
      }
    },
  },
});
